import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
// Utils
import { Form, FormElements } from '@zolteam/zol-react-form';
// Components
import { Box, Button } from '@zolteam/zol-admin-ui';
// Services
import { postAdminUserPasswordResetFinalize } from '../../../services/parazol/parazolService';
// Locales
import fr from '../../../locales/fr.json';

const FORM_NAME = 'resetPassword';

const SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'resetPassword',
  properties: {
    password: {
      type: 'string',
      format: 'password',
      minLength: 1,
      description: 'Nouveau mot de passe',
      x_layout: '1/1/1/13',
    },
  },
  required: [
    'password',
  ],
  additionalProperties: false,
};

/**
 * Forgot Password View
 * @returns {JSX.Element}
 */
function ResetPassword() {
  // Hooks
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // States and variables
  const token = searchParams.get('token');

  // Services
  const {
    mutate: mutateResetPassword,
    isLoading: isLoadingResetPassword,
    isSuccess: success,
  } = useMutation({
    mutationFn: postAdminUserPasswordResetFinalize,
  });

  // Handlers
  /**
   * Submit the form.
   * @param {object} data
   * @param {string} data.password
   * @returns {void}
   */
  const onSubmit = (data) => {
    mutateResetPassword({ ...data, token });
  };

  return (
    <Box direction="column" gap="m" fullWidth>
      {success ? (
        <>
          <Box direction="column" gap="xs" align="center" fullWidth>
            <h1 className="h3-default font-weight-bold">
              {t('cortizol.resetPassword.successTitle')}
            </h1>
            <span className="p1-default">
              {t('cortizol.resetPassword.successDescription')}
            </span>
          </Box>

          <Box direction="column" gap="xs" align="normal" fullWidth>
            <Button color="noBackground">
              <Link to="/login">
                {t('cortizol.forgotPassword.login')}
              </Link>
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box direction="column" gap="xs" align="center" fullWidth>
            <h1 className="h3-default font-weight-bold">
              {t('cortizol.resetPassword.title')}
            </h1>
            <span className="p1-default">
              {t('cortizol.resetPassword.description')}
            </span>
          </Box>

          <Box fullWidth>
            <Form
              name={FORM_NAME}
              className=""
              callbacks={{
                onSubmit,
              }}
              schema={SCHEMA}
              translations={{ formValidation: fr.cortizol.formValidation }}
            >
              {(formConfig, formInfo) => (
                <FormElements
                  formConfig={formConfig}
                  formTitle={formInfo.formName}
                />
              )}
            </Form>
          </Box>

          <Box direction="column" gap="xs" align="normal" fullWidth>
            <Button
              isSubmit
              form={FORM_NAME}
              isLoading={isLoadingResetPassword}
            >
              {t('cortizol.resetPassword.submit')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export default ResetPassword;
